import { LessonsBulkDeleteMode, LessonsDeleteModal } from './enums'
import { useLessonsStore } from './store'
import { useLessonDestroyApi } from '../../api/lessons/destroy.hook'
import { useLessonsBulkDestroyApi } from '../../api/lessons/bulk-destroy.hook'
import { useLessonsRefresh } from './lessons-refresh.hook'

interface ModalLessonsDeleteReturn {
  refIsOpen: ComputedRef<boolean>
  refIsDeleting: ComputedRef<boolean>
  refTitle: ComputedRef<string>
  refDetail: ComputedRef<string>
  refButtonTitle: ComputedRef<string>
  confirm: () => Promise<void>
  close: () => void
}

const pluralSuffix = (count: number): string => (count > 1 ? 's' : '')

/** One confirmation for the three deletes of the tab, with the legacy wording of each. The modal stays open under
 *  its loader until the table is reread, and closes at once on a refusal. */
export const useModalLessonsDelete = (): ModalLessonsDeleteReturn => {
  const route = useRoute()
  const store = useLessonsStore()
  const { refreshLessons } = useLessonsRefresh()

  const disciplineId = route.params.id as string

  const refIsOpen = computed<boolean>(() => store.getDeleteModal !== null)

  const refIsDeleting = computed<boolean>(() => store.isDeleting)

  /** The count as confirmed: the reread under the open modal must not change its wording. */
  const refConfirmedCount = ref<number | null>(null)

  const refCount = computed<number>(() => {
    if (refConfirmedCount.value !== null) return refConfirmedCount.value
    if (store.getDeleteModal === LessonsDeleteModal.Selected) return store.getSelectedIds.length
    if (store.getDeleteModal === LessonsDeleteModal.Reserved) return store.getReservedLessons.length

    return 1
  })

  const refIsReserved = computed<boolean>(() => store.getDeleteModal === LessonsDeleteModal.Reserved)

  const refTitle = computed<string>(() => {
    const suffix = pluralSuffix(refCount.value)

    return refIsReserved.value ? `Delete reserved lesson${suffix}` : `Delete selected lesson${suffix}`
  })

  const refDetail = computed<string>(() => {
    const suffix = pluralSuffix(refCount.value)
    const subject = refIsReserved.value
      ? `reserved lesson${suffix}`
      : (refCount.value > 1 ? `these ${refCount.value} lessons` : 'this lesson')

    return `Are you sure you want to delete ${subject}? All of data will be permanently removed. This action cannot be undone.`
  })

  const refButtonTitle = computed<string>(() => {
    const suffix = pluralSuffix(refCount.value)

    return refIsReserved.value ? `Delete reserved lesson${suffix}` : `Delete lesson${suffix}`
  })

  const close = (): void => {
    if (store.isDeleting) return
    store.closeDeleteModal()
  }

  /** The deleted ids, or null when the request itself was refused. */
  const deleteSingle = async (): Promise<number[] | null> => {
    const lesson = store.getActiveLesson
    if (!lesson) return null

    const isDeleted = await useLessonDestroyApi({ disciplineId, lessonId: lesson.id })

    return isDeleted ? [lesson.id] : null
  }

  const deleteBulk = async (mode: LessonsBulkDeleteMode): Promise<number[] | null> => {
    const result = await useLessonsBulkDestroyApi({
      disciplineId,
      mode,
      ids: mode === LessonsBulkDeleteMode.ByIds ? [...store.getSelectedIds] : undefined,
    })

    return result ? result.deleted : null
  }

  const runDelete = (modal: LessonsDeleteModal | null): Promise<number[] | null> => {
    if (modal === LessonsDeleteModal.Selected) return deleteBulk(LessonsBulkDeleteMode.ByIds)
    if (modal === LessonsDeleteModal.Reserved) return deleteBulk(LessonsBulkDeleteMode.Reserved)

    return deleteSingle()
  }

  const finish = (): void => {
    store.setDeleting(false)
    store.closeDeleteModal()
    refConfirmedCount.value = null
  }

  const confirm = async (): Promise<void> => {
    if (store.isDeleting) return

    const modal = store.getDeleteModal
    refConfirmedCount.value = refCount.value
    store.setDeleting(true)

    const deletedIds = await runDelete(modal)

    if (deletedIds === null) {
      finish()
      return
    }

    if (deletedIds.length) await refreshLessons(deletedIds)
    if (modal === LessonsDeleteModal.Selected) store.clearSelected()

    finish()
  }

  return {
    refIsOpen,
    refIsDeleting,
    refTitle,
    refDetail,
    refButtonTitle,
    confirm,
    close,
  }
}
